import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { LifeBuoy, ShieldCheck, Server, Layers, ListChecks, CheckCircle2, Phone } from 'lucide-react';
import { Eyebrow } from '../ui/Section';
import { Button } from '../ui/Button';
import { CONTACT } from '../../lib/constants';
const areas = [{
  icon: LifeBuoy,
  label: 'Support and ownership',
  status: 'Mapped',
  color: '#0AA77E'
}, {
  icon: ShieldCheck,
  label: 'Security posture',
  status: 'Reviewed',
  color: '#D2223D'
}, {
  icon: Server,
  label: 'Hosting and continuity',
  status: 'Clarify responsibility',
  color: '#57578E'
}, {
  icon: Layers,
  label: 'Software and licensing',
  status: 'Priority identified',
  color: '#008EC6'
}, {
  icon: ListChecks,
  label: 'Priorities and next steps',
  status: 'Recommendation prepared',
  color: '#00ABE4'
}];
const outputs = ['Support model and responsibility map', 'Security posture and Essential Eight considerations', 'Hosting, backup and continuity dependencies', 'Software, licensing and vendor responsibilities', 'Prioritised recommendations in plain English'];
interface FormState {
  name: string;
  email: string;
  firm: string;
  users: string;
}
/** Left column: confidence map + review outputs, stretched to fill full column height. */
function ConfidencePanel() {
  return <div className="flex h-full flex-col gap-6" data-id="element-302">
      <div className="rounded-panel border border-border bg-white p-6 shadow-md md:p-7" data-id="element-303">
        <div className="flex items-center justify-between border-b border-border pb-4" data-id="element-304">
          <p className="font-display text-[16px] font-bold text-navy" data-id="element-305">
            Technology confidence map
          </p>
          <span className="rounded-full bg-surface-3 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-ct-deep" data-id="element-306">
            Review
          </span>
        </div>
        <ul className="mt-4 space-y-3" data-id="element-307">
          {areas.map((a, i) => <motion.li key={a.label} initial={{
          opacity: 0,
          x: -8
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.35,
          delay: i * 0.08
        }} className="flex items-center justify-between rounded-xl border border-border bg-surface-2 px-4 py-3" data-id="element-308">
              <span className="flex items-center gap-3" data-id="element-309">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg" style={{
              background: `${a.color}14`
            }} data-id="element-310">
                  <a.icon className="h-5 w-5" style={{
                color: a.color
              }} aria-hidden data-id="element-311" />
                </span>
                <span className="text-[14px] font-semibold text-ink" data-id="element-312">
                  {a.label}
                </span>
              </span>
              <span className="rounded-full px-2.5 py-1 text-[11px] font-bold" style={{
            background: `${a.color}14`,
            color: a.color
          }} data-id="element-313">
                {a.status}
              </span>
            </motion.li>)}
        </ul>
      </div>

      {/* fills the empty space the client circled — the review outputs live here now */}
      <div className="flex flex-1 flex-col rounded-panel border border-border bg-navy p-6 shadow-md md:p-7" data-id="element-314">
        <p className="text-[13px] font-bold uppercase tracking-[0.12em] text-ct-light" data-id="element-315">
          What the review gives you
        </p>
        <ul className="mt-4 grid flex-1 content-center gap-3" data-id="element-316">
          {outputs.map(o => <li key={o} className="flex items-start gap-3 text-[15px] text-white/90" data-id="element-317">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-ct-light" aria-hidden data-id="element-318" />
              {o}
            </li>)}
        </ul>
      </div>
    </div>;
}
function ReviewForm() {
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    firm: '',
    users: ''
  });
  const [errors, setErrors] = useState<Partial<FormState>>({});
  const [submitted, setSubmitted] = useState(false);
  const validate = () => {
    const e: Partial<FormState> = {};
    if (!form.name.trim()) e.name = 'Please enter your name.';
    if (!form.email.trim()) e.email = 'Please enter your work email.';else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email address.';
    if (!form.firm.trim()) e.firm = 'Please enter your firm name.';
    if (!form.users.trim()) e.users = 'Please enter an approximate user count.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const submit = (ev: React.FormEvent) => {
    ev.preventDefault();
    if (validate()) setSubmitted(true);
  };
  const field = (id: keyof FormState, label: string, type: string, autoComplete: string, inputMode?: 'numeric') => <div data-id="element-319">
      <label htmlFor={id} className="block text-[14px] font-semibold text-ink" data-id="element-320">
        {label}
      </label>
      <input id={id} name={id} type={type} autoComplete={autoComplete} inputMode={inputMode} value={form[id]} onChange={e => setForm({
      ...form,
      [id]: e.target.value
    })} aria-invalid={!!errors[id]} aria-describedby={errors[id] ? `${id}-error` : undefined} className={`mt-1.5 min-h-[48px] w-full rounded-xl border bg-white px-4 text-[15px] text-ink outline-none transition-colors focus:border-ct-cyan focus:ring-2 focus:ring-ct-cyan/25 ${errors[id] ? 'border-security' : 'border-border'}`} data-id="element-321" />
      {errors[id] && <p id={`${id}-error`} className="mt-1 text-[13px] text-security" data-id="element-322">
          {errors[id]}
        </p>}
    </div>;
  if (submitted) {
    return <div className="flex h-full flex-col items-center justify-center rounded-panel border border-border bg-white p-8 text-center shadow-md" data-id="element-323">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-managed/15" data-id="element-324">
          <CheckCircle2 className="h-7 w-7 text-managed-deep" aria-hidden data-id="element-325" />
        </span>
        <h3 className="mt-5 font-display text-[22px] font-bold text-navy" data-id="element-326">
          Request received
        </h3>
        <p className="mt-3 max-w-sm text-[15px] leading-relaxed text-slate" data-id="element-327">
          Thank you, {form.name.split(' ')[0] || 'there'}. A member of the CT
          Group team will be in touch to arrange your Independent IT Review
          conversation.
        </p>
        <p className="mt-4 text-[14px] text-slate" data-id="element-328">
          Prefer to speak sooner? Call{' '}
          <a href={CONTACT.phoneHref} className="font-semibold text-ct-deep" data-id="element-329">
            {CONTACT.phone}
          </a>
          .
        </p>
      </div>;
  }
  return <form onSubmit={submit} noValidate className="flex h-full flex-col rounded-panel border border-border bg-white p-6 shadow-md md:p-8" data-id="element-330">
      <p className="font-display text-[18px] font-bold text-navy" data-id="element-331">
        Request your IT Review
      </p>
      <div className="mt-5 space-y-4" data-id="element-332">
        {field('name', 'Your name', 'text', 'name')}
        {field('email', 'Work email', 'email', 'email')}
        {field('firm', 'Firm name', 'text', 'organization')}
        {field('users', 'Approximate user count', 'text', 'off', 'numeric')}
      </div>

      <Button type="submit" variant="primary" full className="mt-6" data-id="element-333">
        Request an IT Review conversation
      </Button>

      <p className="mt-4 text-[12px] leading-relaxed text-slate" data-id="element-334">
        By sending this request, you agree that CT Group may contact you about
        the IT Review. Your information will be handled in line with the Privacy
        Policy.
      </p>

      <div className="mt-auto flex items-center gap-2 border-t border-border pt-5 text-[14px] text-slate" data-id="element-335">
        <Phone className="h-4 w-4 text-ct-cyan" aria-hidden data-id="element-336" />
        Prefer to speak first? Call{' '}
        <a href={CONTACT.phoneHref} className="font-semibold text-ct-deep" data-id="element-337">
          {CONTACT.phone}
        </a>
        .
      </div>
    </form>;
}
export function ITReview() {
  return <section id="it-review" className="w-full bg-white py-16 md:py-24 lg:py-28" aria-label="Independent IT Review" data-id="element-338">
      <div className="mx-auto max-w-content px-5 md:px-8" data-id="element-339">
        {/* Heading spans full width, as marked in the client screenshot */}
        <div className="max-w-3xl" data-id="element-340">
          <Eyebrow data-id="element-341">The signature first step</Eyebrow>
          <h2 className="mt-5 font-display text-[30px] font-black leading-[1.08] tracking-tight text-navy md:text-[42px] lg:text-[46px]" data-id="element-342">
            See the risk before it becomes disruption.
          </h2>
          <p className="mt-5 max-w-2xl text-[17px] leading-relaxed text-slate md:text-[19px]" data-id="element-343">
            The Independent IT Review gives decision-makers a clear view of what
            is working, where ownership is unclear and which technology issues
            deserve attention first.
          </p>
        </div>

        {/* Two equal-height columns, bottoms perfectly aligned */}
        <div className="mt-12 grid grid-cols-1 items-stretch gap-6 lg:grid-cols-2 lg:gap-8" data-id="element-344">
          <div className="order-2 lg:order-1" data-id="element-345">
            <ConfidencePanel data-id="element-346" />
          </div>
          <div className="order-1 lg:order-2" data-id="element-347">
            <ReviewForm data-id="element-348" />
          </div>
        </div>
      </div>
    </section>;
}